import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FlaggedIcon } from "../../assets/icons";
import {
  addFlaggedSubmission,
  selectFlaggedSubmissions,
} from "../../redux/slice/flaggedSlice";
import { Api } from "../../network/api";
import WatchDemoVideoButton from "../../component/shared/WatchDemoVideoButton";

const Submissions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const flaggedSubmissions = useSelector(selectFlaggedSubmissions);
  const [submissions, setSubmissions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState({});


  const fetchSubmissions = async () => {
    try {
      const response = await Api.getSubmissions();
      const mappedSubmissions =
        response?.data?.submissions?.map((submission) => ({
          id: submission.id,
          userName: submission.user?.name || "N/A",
          assignmentName: submission.subject || "N/A",
          submissionDate: submission.created_at
            ? new Date(submission.created_at).toLocaleDateString()
            : "N/A",
          status: submission.status || "pending",
          isFlagged: submission.is_flag || false,
          score: submission.score || 0,
          user: submission.user,
          subject: submission.subject,
          original_filename: submission.original_filename,
          created_at: submission.created_at,
        })) || [];

      setSubmissions(mappedSubmissions);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      setSubmissions([]);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const toggleFlag = async (submission) => {
    try {
      if (!submission.isFlagged) {
        await Api.addFlag(submission.id);
        dispatch(addFlaggedSubmission(submission));
        fetchSubmissions();
      }
    } catch (error) {
      console.error("Error updating flag status:", error);
    }
  };

  const canShowAction = () => {
    return true;
  };

  const handleOpenDetails = (result) => {
    navigate(`/submissions/${result.id}`, {
      state: {
        userName: result.userName,
        submissionDate: result.submissionDate,
        assignmentName: result.assignmentName,
        status: result.status,
        isFlagged: result.isFlagged
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-300";
      case "pending":
        return "bg-gray-100 text-gray-800 border-gray-300";
      case "reject":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };



  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      setLoadingStatus((prev) => ({ ...prev, [submissionId]: true }));
      const response = await Api.updateStatus(submissionId, {
        status: newStatus,
      });

      if (response.data) {
        setSubmissions((prev) =>
          prev.map((sub) =>
            sub.id === submissionId ? { ...sub, status: newStatus } : sub
          )
        );
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [submissionId]: false }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Submissions</h1>
              <p className="text-gray-600 mt-1">
                View and manage your submissions essay results.
              </p>
            </div>
            <WatchDemoVideoButton
              videoUrl={"https://calbarbible-static.s3.us-east-1.amazonaws.com/static/submission+and+flaged+essay.mov"}
              title="Submissions and Flagged Demo Video"
            />
          </div>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <div className="h-[700px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      USER NAME
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      ASSIGNMENT NAME
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      SUBMISSION DATE
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      FLAG
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      ACTION
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      ACTION
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {submissions && submissions.length > 0 ? (
                    submissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {submission?.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission?.assignmentName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission?.submissionDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <select
                          value={submission.status}
                          onChange={(e) =>
                            handleStatusUpdate(submission.id, e.target.value)
                          }
                          disabled={loadingStatus[submission.id]}
                          className={`px-3 py-1 text-xs font-medium rounded-full border-2 cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            loadingStatus[submission.id]
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:shadow-md hover:scale-105"
                          } ${getStatusColor(submission.status)}`}
                        >
                          <option
                            value="pending"
                            className="bg-white text-gray-900"
                          >
                            Pending
                          </option>
                          <option
                            value="approved"
                            className="bg-white text-gray-900"
                          >
                            Approved
                          </option>
                          <option
                            value="reject"
                            className="bg-white text-gray-900"
                          >
                            Rejected
                          </option>
                        </select>
                        {loadingStatus[submission.id] && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full">
                            <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          toggleFlag(submission);
                        }}
                        className={`p-2 rounded-full transition-colors cursor-pointer ${
                          submission.isFlagged
                            ? "bg-red-600 text-white hover:bg-red-700 shadow-md"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                        }`}
                        title={
                          submission.isFlagged ? "Already flagged" : "Add flag"
                        }
                      >
                        <FlaggedIcon className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {canShowAction() ? (
                        <button
                          onClick={() => handleOpenDetails(submission)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                        >
                          Details
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4">
                        <div className="flex items-center justify-center h-[600px]">
                          <p className="text-gray-500 text-lg">No submissions available</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Submissions;
